/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/alt-text */
// MeetingEventModal.jsx — "Manage your booking" popup for an existing meeting
// on the side-by-side room view. Mirrors WeeklyCalendarEvent's PostEventModal
// (spotType 3 branch): same layout, same buttons, same visibility rules.
import { useTranslation } from "react-i18next";

import { Images } from "../Common/Images.jsx";
import { formatDateToEEEddMMM, formatDateYYYYMMDD, formatReactString } from "../../utils/CommonUtils.js";

// Which actions a meeting offers — the same rules PostEventModal applies to
// SpotType 3 bookings:
//   organiser   -> Edit (gated by approval status when approvers exist) + Cancel
//   participant -> Decline
// Actions need the user's own Bookings[] row (MeetingId, MeetingRepeat, ...).
export const getMeetingActionFlags = ({ room, meeting }) => {
    const booking = meeting?.booking;
    const hasBooking = Boolean(booking);
    const isOrganiser = hasBooking && Boolean(meeting.isOrganiser);

    const meetingApproveStatus = Number(booking?.MeetingApproveStatus);
    const canEditByApproval =
        meetingApproveStatus === 0 ||
        meetingApproveStatus === 5 ||
        (Number(booking?.IsApprover) === 1 && meetingApproveStatus === 1);
    const hasApprovers = Number(booking?.IsApproversExist) === 1;

    return {
        showGoToMap: room?.mapExist === 1 && room?.mapType !== 1,
        showDecline: hasBooking && !isOrganiser,
        showEdit: isOrganiser && (hasApprovers ? canEditByApproval : true),
        showEditSeries: Number(booking?.MeetingRepeat) === 1,
        showCancel: isOrganiser,
    };
};

function MeetingEventModal({
    room,
    meeting,
    selectedDate,
    userName,
    isLoading,
    onClose,
    onGoToMap,
    onEdit,
    onCancel,
    onDecline,
}) {
    const { t } = useTranslation("common");

    const booking = meeting.booking || {};
    const flags = getMeetingActionFlags({ room, meeting });

    const bookingDateStr = formatDateYYYYMMDD(selectedDate);
    const displayDay = selectedDate.getDate();
    const displayMonth = selectedDate.toLocaleDateString("en-US", { month: "long" });
    const displayYear = selectedDate.getFullYear();

    const repeatOption = booking.RepeatOption;
    const repeatIcon = !repeatOption
        ? null
        : repeatOption === "0"
            ? Images.TimeIcon
            : Images.RepeatEventClock;

    const titleText = formatReactString(t("YourBookingForSpace"), [room.spotName], {
        defaultRenderer: () => <span className="color-dark-black">{room.spotName}</span>,
    });

    const guard = (handler) => (e) => {
        e.preventDefault();
        if (!isLoading) handler();
    };

    return (
        <div>
            <div
                className="modal ctm-modalscroll-modal"
                style={{ display: "block", backgroundColor: "rgba(217, 217, 217, 0.59)" }}
                data-bs-backdrop="static"
                data-bs-keyboard="false"
            >
                <div className="modal-dialog modal-dialog-centered modal-custom-width">
                    <div className="modal-content modal-radius">
                        <div className="modal-body">
                            <div className="ctm-modal-header">
                                <div className="close-modal">
                                    <a onClick={onClose}>
                                        <img src={Images.Close} aria-label="Close" />
                                    </a>
                                </div>

                                <div className="pt-4">
                                    <h5 className="popup-title">{titleText}</h5>
                                </div>

                                <div className="date-with-credit d-flex align-items-center justify-content-between pt-2">
                                    <div className="selected-current-date">
                                        <span>{displayDay}</span>
                                        <span>{t(displayMonth)} {displayYear}</span>
                                    </div>
                                </div>

                                <div className="divider-line mt-3"></div>
                            </div>

                            <div className="modal-des">
                                <div className="modal-scroll-inner">
                                    <div className="icon-with-list">
                                        <div className="icon-wrap">
                                            <img src={Images.ZoneMap} alt="map" />
                                            <h4>{room.zoneName}</h4>
                                        </div>

                                        <div className="icon-wrap">
                                            {repeatIcon && <img src={repeatIcon} alt="repeat" />}
                                            <h4>
                                                {meeting.startTime} - {meeting.endTime},{" "}
                                                <span>{formatDateToEEEddMMM(bookingDateStr)}</span>
                                            </h4>
                                        </div>

                                        <div className="icon-wrap meeting-icon">
                                            <h4 className="ps-3 ms-3">
                                                {meeting.meetingName} <span>({meeting.organizer})</span>
                                            </h4>
                                        </div>

                                        <div className="icon-wrap">
                                            <img src={Images.Participant} />
                                            <h4>{userName}</h4>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="ctm-modal-footer">
                                {flags.showGoToMap && (
                                    <a
                                        onClick={() => onGoToMap(room)}
                                        className="green-outline-with-icon w-100 mw-100 min-height-56 selected"
                                    >
                                        <img src={Images.GreenMap} alt="Map" />
                                        <span>{t("gotomap")}</span>
                                    </a>
                                )}

                                {flags.showDecline && (
                                    <div>
                                        <a href="#" onClick={guard(onDecline)} className="main-btn fill-red-btn w-100 mw-100">
                                            <img src={Images.Decline} className="img-fluid" />
                                            <span>{t("react_decline")}</span>
                                        </a>
                                    </div>
                                )}

                                {flags.showEdit && (
                                    <>
                                        <div className="mb-3">
                                            <a
                                                href="#"
                                                onClick={guard(() => onEdit(2))}
                                                className="green-outline-with-icon w-100 mw-100 min-height-56"
                                            >
                                                <img src={Images.EditEventIcon} alt="" className="img-fluid" />
                                                <span>{t("react_edit_this_event")}</span>
                                            </a>
                                        </div>

                                        {flags.showEditSeries && (
                                            <div className="mb-3">
                                                <a
                                                    href="#"
                                                    onClick={guard(() => onEdit(1))}
                                                    className="green-outline-with-icon w-100 mw-100 min-height-56"
                                                >
                                                    <img src={Images.EditAllSeriesIcon} alt="" className="img-fluid" />
                                                    <span>{t("react_edit_all_events_in_series")}</span>
                                                </a>
                                            </div>
                                        )}
                                    </>
                                )}

                                {flags.showCancel && (
                                    <a className="main-btn cancel-btn gap-2" onClick={guard(onCancel)}>
                                        <span> {t("cancel_booking")}</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MeetingEventModal;
